// eslint-disable-next-line no-unused-vars
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { darken, transparentize, transitions } from 'polished';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { TableCell } from '../TableCell';
import { DataTableActions } from '../../../DataTable/components/DataTableActions';

const StyledTr = styled.tr`
    background-color: ${(props) => props.theme.colors.background.default};
    margin: 0;
    padding: 0;
    ${transitions(['background-color'], '0.2s ease-in 0.05s')};
    ${({ clickable }) =>
        clickable &&
        css`
            cursor: pointer;
        `}
    td {
        border: none;
        border-bottom: 1px solid ${(props) => props.theme.colors.border.default};
        ${(props) =>
            props.dsi
                ? `span { color: ${darken(
                      0.25,
                      props.dsi === 'created'
                          ? props.theme.colors.intent.success
                          : props.dsi === 'edited'
                          ? props.theme.colors.intent.info
                          : props.theme.colors.intent.danger
                  )}; text-decoration: ${props.dsi === 'deleted' ? `line-through` : `none`}; text-shadow: 0 1px 0 ${
                      props.dsi === 'created' || props.dsi === 'edited'
                          ? props.dsi === 'created'
                              ? transparentize(0.42, props.theme.colors.intent.success)
                              : transparentize(0.42, props.theme.colors.intent.info)
                          : `transparent`
                  }}`
                : ``}
    }
    :nth-child(2n) {
        background-color: ${(props) => props.theme.colors.background.tint};
    }
    ${({ selected }) =>
        selected &&
        css`
            background-color: ${(props) => props.theme.colors.brand.P1};
            :nth-child(2n) {
                background-color: ${(props) => `${darken(0.05, props.theme.colors.brand.P1)} !important`};
            }
            &:hover {
                background-color: ${(props) => `${darken(0.1, props.theme.colors.brand.P1)} !important`};
            }
        `}
    ${({ isdragging }) =>
        isdragging === 'true' &&
        css`
            background: rgba(127, 207, 250, 0.3) !important;
            color: rgba(127, 207, 250, 0.3) !important;
        `}
`;

const RenderDataTableCell = ({ datum, dragHandleProps, ...props }) => {
    return (
        <TableCell
            actions={typeof datum.column.DataTableActions !== 'undefined'}
            style={{
                ...(datum.column?.renderOptions?.columnStyles || {})
            }}
        >
            {typeof datum.column.renderOptions !== 'undefined' ? (
                datum.column.renderOptions?.values[datum.value]
            ) : typeof datum.column.DataTableActions !== 'undefined' ? (
                <DataTableActions actions={datum.value} dragHandleProps={dragHandleProps} />
            ) : (
                datum.render('Cell')
            )}
        </TableCell>
    );
};

RenderDataTableCell.propTypes = {
    datum: PropTypes.object,
    dragHandleProps: PropTypes.object
};

export const TableRowDragSort = ({ id, cells, ...props }) => {
    const a11yProps = {};
    const { attributes, listeners, transform, transition, setNodeRef, isDragging } = useSortable({
        id: id,
        animateLayoutChanges: () => false
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition
    };
    const styleProps = props.onClick
        ? {
              clickable: true
          }
        : {};

    return (
        <StyledTr
            ref={setNodeRef}
            {...a11yProps}
            {...props}
            {...styleProps}
            style={style}
            isdragging={isDragging?.toString() ?? 'false'}
        >
            {cells.map((cell) => (
                // eslint-disable-next-line react/jsx-key
                <RenderDataTableCell
                    datum={cell}
                    {...cell.getCellProps()}
                    dragHandleProps={{ attributes, listeners, isDragging }}
                />
            ))}
        </StyledTr>
    );
};

export const TableRow = ({ children, ...props }) => {
    const a11yProps = {};
    const styleProps = props.onClick
        ? {
              clickable: true
          }
        : {};

    return (
        <StyledTr {...a11yProps} {...props} {...styleProps}>
            {children}
        </StyledTr>
    );
};

const TableRowProps = {
    onClick: PropTypes.func
};

TableRow.propTypes = { ...TableRowProps, children: PropTypes.node.isRequired };
TableRowDragSort.propTypes = { ...TableRowProps, id: PropTypes.string, cells: PropTypes.array };

TableRow.defaultProps = {};
TableRowDragSort.defaultProps = {};
